import api from "@/lib/api";

const SW_URL = "/showroom-sw.js";
const VERSION = 4;
const checkedWorkerScopes = new Set();
const SLOT_KEY = (slug) => `evb.showroom.${slug}`;
const readRecord = (slug) => {
  try { return JSON.parse(localStorage.getItem(SLOT_KEY(slug)) || "null"); } catch (_) { return null; }
};
const writeRecord = (slug, record) => {
  try { localStorage.setItem(SLOT_KEY(slug), JSON.stringify(record)); } catch (_) { /* private mode */ }
};

function updateExistingWorker(registration) {
  if (navigator.onLine === false || typeof registration?.update !== "function") return;
  try {
    const script = new URL(registration.active?.scriptURL);
    if (script.origin !== window.location.origin || script.pathname !== SW_URL) return;
    const scope = registration.scope || script.href;
    if (checkedWorkerScopes.has(scope)) return;
    checkedWorkerScopes.add(scope);
    try {
      // Existing clients should discover fixes without downloading the project
      // again. Do not block status/offline use, reload the page, or clear data.
      Promise.resolve(registration.update()).catch(() => checkedWorkerScopes.delete(scope));
    } catch (_) {
      checkedWorkerScopes.delete(scope);
    }
  } catch (_) { /* Missing/foreign worker metadata: leave its registration alone. */ }
}

export function isShowroomActive() {
  try {
    const slug = /^\/(?:p|embed)\/([^/]+)/.exec(window.location.pathname)?.[1];
    const record = slug && readRecord(decodeURIComponent(slug));
    return !!navigator.serviceWorker?.controller && record?.cacheVersion === VERSION && record?.ready === true;
  } catch (_) { return false; }
}

export function isSupported() {
  return typeof navigator !== "undefined" && "serviceWorker" in navigator && typeof caches !== "undefined";
}

export async function registerShowroom() {
  if (!isSupported()) return null;
  const reg = await navigator.serviceWorker.register(SW_URL, { scope: "/", updateViaCache: "none" });
  const incoming = reg.installing || reg.waiting;
  if (incoming && incoming.state !== "activated") {
    await new Promise((resolve, reject) => {
      const timer = setTimeout(() => finish(new Error("Service worker activation timed out")), 30000);
      const changed = () => {
        if (incoming.state === "activated") finish();
        if (incoming.state === "redundant") finish(new Error("Service worker installation failed"));
      };
      const finish = (error) => {
        clearTimeout(timer); incoming.removeEventListener("statechange", changed);
        if (error) reject(error); else resolve();
      };
      incoming.addEventListener("statechange", changed); changed();
    });
  }
  await navigator.serviceWorker.ready;
  return reg;
}

async function postToSW(msg, onProgress) {
  const controller = (await navigator.serviceWorker.ready).active || navigator.serviceWorker.controller;
  if (!controller) throw new Error("No active service worker");
  return new Promise((resolve, reject) => {
    const channel = new MessageChannel();
    let timeout;
    const finish = (error, data) => {
      clearTimeout(timeout); channel.port1.close();
      if (error) reject(error); else resolve(data);
    };
    const resetTimeout = () => { clearTimeout(timeout); timeout = setTimeout(() => finish(new Error("Download timed out")), 150000); };
    resetTimeout();
    channel.port1.onmessage = (event) => {
      const data = event.data || {};
      if (data.type === "error") return finish(new Error(data.message || "Download failed"));
      if (["done", "status", "cleared"].includes(data.type)) {
        if (data.version !== VERSION) return finish(new Error("Please refresh to update offline support"));
        return finish(null, data);
      }
      if (data.type === "progress") { resetTimeout(); onProgress?.(data); }
    };
    channel.port1.onmessageerror = () => finish(new Error("Download communication failed"));
    try { controller.postMessage(msg, [channel.port2]); } catch (error) { finish(error); }
  });
}

async function appShellUrls(slug) {
  const response = await fetch("/asset-manifest.json", { cache: "no-cache" });
  if (!response.ok) throw new Error("Application assets unavailable");
  const manifest = await response.json();
  const assets = Object.values(manifest.files || {}).filter((value) => typeof value === "string"
    && !/\.(map|txt)(\?|$)/i.test(value));
  if (!assets.some((value) => /\.js(\?|$)/.test(value))) throw new Error("Application scripts missing");
  const shellUrl = new URL(`/p/${encodeURIComponent(slug)}`, window.location.origin).href;
  return { shellUrl, urls: [shellUrl, "/asset-manifest.json", ...assets]
    .map((url) => new URL(url, window.location.origin).href)
    .filter((url) => new URL(url).origin === window.location.origin) };
}

export async function precacheProject(slug, onProgress) {
  if (!isSupported()) throw new Error("Offline viewing is not supported");
  await registerShowroom();
  const response = await api.get(`/public/projects/${slug}/manifest`);
  const manifest = response.data || {};
  const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const apiUrls = [...(manifest.api_endpoints || []), `/api/public/projects/${encodeURIComponent(slug)}/manifest`]
    .map((url) => /^https?:/.test(url) ? url : apiBase + url);
  const shell = await appShellUrls(slug);
  const urls = [...new Set([...shell.urls, ...(manifest.media_urls || []), ...apiUrls]
    .filter(Boolean).map((url) => new URL(url, window.location.origin).href))];
  writeRecord(slug, { cacheVersion: VERSION, ready: false, pending: true, total: urls.length });
  const result = await postToSW({ type: "precache", slug, urls, shellUrl: shell.shellUrl }, onProgress);
  writeRecord(slug, { cacheVersion: VERSION, ready: result.ready, updatedAt: Date.now(),
    total: result.total, cached: result.done, failed: result.failed, version: manifest.version });
  return result;
}

export async function getShowroomStatus(slug) {
  const record = readRecord(slug);
  const empty = { enabled: false, cached: 0, total: record?.total || 0, failed: 0,
    partial: !!record, needsRefresh: !!record && record.cacheVersion !== VERSION };
  if (!isSupported()) return empty;
  const reg = await navigator.serviceWorker.getRegistration("/");
  if (!reg?.active) return empty;
  updateExistingWorker(reg);
  try {
    const result = await postToSW({ type: "status", slug });
    if (record || result.total > 0) writeRecord(slug, { ...record, cacheVersion: VERSION, ready: result.ready,
      cached: result.cached, total: result.total, failed: result.failed, updatedAt: result.updatedAt });
    return { enabled: result.ready, cached: result.cached, total: result.total, failed: result.failed,
      partial: !result.ready && (!!record || result.total > 0), needsRefresh: !!record && result.total === 0 };
  } catch (_) { return empty; }
}

export async function disableShowroom(slug) {
  if (isSupported()) await caches.delete(`evb-showroom-v4:${encodeURIComponent(slug)}`);
  try { localStorage.removeItem(SLOT_KEY(slug)); } catch (_) { /* private mode */ }
}
